// src/components/roads/ponding/pondingUi.js — shared tokens + formatters for the road ponding forecast.
export const BAND_META = Object.freeze({
    none: { key: 'none', label: 'Clear', scheme: 'green', hex: '#38A169', weight: 3, meaning: 'No standing water forecast.' },
    watch: { key: 'watch', label: 'Watch', scheme: 'cyan', hex: '#00A3C4', weight: 6, meaning: 'Standing water 2–10 cm or gutter flow into the lane — aquaplaning risk.' },
    high: { key: 'high', label: 'High', scheme: 'blue', hex: '#3182CE', weight: 8, meaning: '10–20 cm — kerb nearly full, difficult for small vehicles.' },
    severe: { key: 'severe', label: 'Severe', scheme: 'purple', hex: '#805AD5', weight: 10, meaning: '20 cm or more — impassable for cars, close or divert.' },
});
export const BAND_KEYS = ['watch', 'high', 'severe'];
export const SIGNAL_META = Object.freeze({
    none: { scheme: 'green', label: 'No road ponding forecast' },
    advisory: { scheme: 'cyan', label: 'Water on some roads' },
    watch: { scheme: 'blue', label: 'Roads ponding' },
    warning: { scheme: 'purple', label: 'Roads impassable' },
});
export const SIGNAL_RANK = { none: 0, advisory: 1, watch: 2, warning: 3 };
export const KIND_LABEL = Object.freeze({
    sag: 'Low point (sag)', drain_overflow: 'Drain overflowing onto the road', gutter_flow: 'Gutter flow into the lane', flat: 'Flat stretch',
});
export const bandMeta = (b) => BAND_META[b] || BAND_META.none;

export const fmtDepth = (m) => (m == null ? '—' : m >= 0.1 ? `${(m * 100).toFixed(0)} cm` : `${(m * 100).toFixed(1)} cm`);
export const fmtHours = (h) => (h == null ? '—' : h < 1 ? 'now' : `+${Math.round(h)} h`);
export const fmtClears = (s) => {
    if (!s) return '—';
    if (s.stands_beyond_window) return 'stands > 48 h after the rain';
    if (s.kind === 'gutter_flow' && !s.peak_depth_m) return 'runs off when the rain stops';
    if (s.clears_after_rain_hours == null) return '—';
    return s.clears_after_rain_hours <= 1 ? 'drains within 1 h' : `drains ${Math.round(s.clears_after_rain_hours)} h after the rain`;
};
/** What to show for a segment's water: standing depth, or — for gutter flow with no standing water — the spread into the lane. */
export const fmtWater = (s, hour = null) => {
    const d = hour == null ? (s?.peak_depth_m ?? 0) : (s?.hourly_depth_m?.[hour] ?? 0);
    if (s?.kind === 'gutter_flow' && !(d > 0.005) && s?.gutter_spread_m) return `${Number(s.gutter_spread_m).toFixed(1)} m spread`;
    return fmtDepth(d);
};
/** Depth of a segment at an hour (null hour = peak). */
export const depthAt = (s, hour) => (hour == null ? (s.peak_depth_m ?? 0) : (s.hourly_depth_m?.[hour] ?? 0));
/** Band at an hour (gutter-flow segments keep their band — spread is not in the hourly series). */
export const bandAt = (s, hour) => {
    if (hour == null || s.kind === 'gutter_flow') return s.band;
    const d = depthAt(s, hour);
    return d >= 0.2 ? 'severe' : d >= 0.1 ? 'high' : d >= 0.02 ? 'watch' : 'none';
};
export const timeLabel = (iso) => {
    const t = Date.parse(iso);
    return Number.isFinite(t) ? new Date(t).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
};
